// Product analytics (PostHog) — PRODUCTION ONLY.
//
// The key lives ONLY in `.env.production`, so it's absent in dev: the dev server
// loads `.env.development` (no key) and `initAnalytics()` no-ops. It only initializes
// in the prod build, which app-prod serves. Write-only client key (safe to ship
// publicly). Called once from the app entry (main.ts).
import posthog from 'posthog-js';

// Bracket access matches how the app already reads env (see admin.ts) and keeps
// svelte-check happy without a custom ImportMetaEnv declaration.
const KEY = import.meta.env['VITE_POSTHOG_KEY'] as string | undefined;

export function initAnalytics(): void {
  if (!KEY) return; // no key → dev / any non-prod build → analytics stays off
  posthog.init(KEY, {
    api_host: 'https://us.i.posthog.com',
    defaults: '2026-05-30',
    person_profiles: 'identified_only',
  });
}

// The distinct_id we last identified, so repeated calls (fetchMe runs on almost
// every route mount) don't fire a redundant $identify each time.
let identified: string | null = null;

/**
 * Tie the current session (and its recording) to a real person, so PostHog's
 * People / Recordings dashboards show the user by email instead of an anonymous
 * id. Called from admin.ts on login / register / fetchMe. No-ops without a key
 * (dev) and skips when the same user is already identified. Email is the stable
 * distinct_id; email/name/role ride along as person properties for filtering.
 */
export function identifyUser(user: { email?: string; name?: string; role?: string }): void {
  if (!KEY || !user?.email || identified === user.email) return;
  identified = user.email;
  posthog.identify(user.email, {
    email: user.email,
    ...(user.name ? { name: user.name } : {}),
    ...(user.role ? { role: user.role } : {}),
  });
}

/**
 * Drop the identified person on logout / session clear, so a shared browser
 * doesn't attribute the next session to the previous user. Called from
 * admin.ts `clearToken` (the single logout + 401-expiry path).
 */
export function resetAnalytics(): void {
  if (!KEY) return;
  identified = null;
  posthog.reset();
}
